import { PlusIcon } from "lucide-react";
import React, { useContext, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { Button } from "./ui/button";
import { LanguageProviderContext } from "@/providers/LanguageProvider";
import ClientsCard from "./clients/ClientsCard";
import ClientsDialog from "./clients/ClientsDialog";
import { useClientMutations } from "@/hooks/useClientMutations";
import { useClientsPage } from "@/hooks/useClientsPage";
import { useToast } from "@/hooks/use-toast";
import ClientsDetailModal from "./clients/ClientsDetailModal";
import { type Client } from "@/types";
import ClientsSearchBar from "./clients/ClientsSearchBar";
import PaginationComponent from "./ui/PaginationComponent";
import { BoltLoader } from "@/components/ui/bolt-loader";

const CLIENTS_PAGE_SIZE = 20;

export default function ClientsManagment() {

  const clientForm:Client = {
    id: "",
    organizationId: "",
    name: "",
    tipo: "",
    identification: "",
    email: "",
    phone: {
      country_code: "",
      number: "",
    },
    address: {
      province: "",
      canton: "",
      district: "",
      other: "",
    },
  }

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [clientFormData, setClientFormData] = useState<Client>(clientForm);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const {
    createClient,
    updateClient,
    deleteClient,
    isCreating,
    isUpdating,
    findClientByIdentification,
  } = useClientMutations();
  const { clients: pagedClients, total, isLoading, error } = useClientsPage(currentPage, debouncedSearch);
  const totalPages = Math.max(1, Math.ceil(total / CLIENTS_PAGE_SIZE));
  if (currentPage > totalPages) {
    setCurrentPage(totalPages);
  }

  const updateDebouncedSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearch(value);
    setCurrentPage(1);
  }, 300);
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const [isClientDetailModalOpen, setIsClientDetailModalOpen] = useState(false);
  const { translator } = useContext(LanguageProviderContext);
  const { toast } = useToast();
  const resetForm = () => {
    setClientFormData(clientForm);
    setEditingClient(null);
  };

  const openClientDialog = () => {
    resetForm();
    setIsClientDialogOpen(true);
  };

  const saveClient = async (client: Client) => {
    if (!client.name.trim()) {
      return;
    }
    const existing = await findClientByIdentification(client.identification ?? '');
    if (existing && existing.id !== editingClient?.id) {
      toast({
        title: translator("Global.error") || "Error",
        description: translator("Clients.clientAlreadyExists") || "A client with this identification already exists.",
        variant: "destructive",
      });
      return;
    }
    if (editingClient) {
      updateClient({
        ...editingClient,
        ...client,
      });
    } else {
      createClient(client);
    }
    resetForm();
    setIsClientDialogOpen(false);
  };

  const openEditDialog = (client: Client) => {
    setEditingClient(client);
    setClientFormData(client);
    setIsClientDialogOpen(true);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsClientDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    updateDebouncedSearch(value);
  };

  const clearSearch = () => {
    updateDebouncedSearch.cancel();
    setSearchTerm("");
    setDebouncedSearch("");
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Header Client*/}
      <div
        className="flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0"
      >
        <div>
          <h2 className="text-3xl font-bold text-foreground">
            {translator("Clients.clients")}
          </h2>
          <p className="text-muted-foreground">
            {translator("Clients.clientsDesc")}
          </p>
        </div>
        <div>
          <ClientsDialog
            isOpen={isClientDialogOpen}
            onOpenChange={handleDialogOpenChange}
            formData={clientFormData}
            saveClient={(client: Client) => { void saveClient(client); }}
            isSubmitting={isCreating || isUpdating}
          >
            <Button
              className="flex items-center space-x-2 w-full"
              onClick={openClientDialog}
            >
              <PlusIcon className="h-4 w-4" />
              {translator("Clients.addClient")}
            </Button>
          </ClientsDialog>
        </div>
      </div>
      {/* Clients List */}
      <div className="w-full mt-4">
      <ClientsSearchBar
        searchTerm={searchTerm}
        setSearchTerm={handleSearchChange}
        resultCount={total}
        clearSearch={clearSearch}
      />
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <BoltLoader size="md" />
            <span className="text-muted-foreground">{translator("Global.loading")}</span>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-destructive">
            {translator("Global.error")}
          </div>
        ) : pagedClients.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {translator("Clients.noClientsFound")}
          </div>
        ) : (
          <div className="space-y-2 w-full mt-4">
            {pagedClients.map((client) => (
              <ClientsCard
                key={client.id}
                client={client}
                onEdit={openEditDialog}
                onDelete={deleteClient}
                onShowInfo={(client) => {
                  setSelectedClient(client);
                  setIsClientDetailModalOpen(true);
                }}
              />
            ))}
          </div>
        )}
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={CLIENTS_PAGE_SIZE}
          totalItems={total}
          onPageChange={setCurrentPage}
          maxVisiblePages={5}
          showItemsInfo
          className="mt-4"
          previousLabel={translator('Sales.previous')}
          nextLabel={translator('Sales.next')}
          renderItemsInfo={({ startItem, endItem, totalItems: last }) =>
            `${translator('Sales.showing')} ${startItem} ${translator('Sales.to')} ${endItem} ${translator('Sales.of')} ${last} ${translator('Sales.results')}`
          }
        />
      </div>
      <ClientsDetailModal
        isOpen={isClientDetailModalOpen}
        onOpenChange={setIsClientDetailModalOpen}
        client={selectedClient}
      />
    </div>
  );
}
